import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * Global flag — when true, ALL beforeunload handlers are bypassed.
 * Set directly (no async event dispatch) for guaranteed timing.
 */
let globalBypass = false;

/**
 * Call before window.location.href / window.location.reload() to prevent
 * the browser's native "Leave site?" dialog.
 */
export function bypassNavigationBlock() {
  globalBypass = true;
}

/**
 * Custom hook to block navigation when the form has unsaved changes.
 * Works with BrowserRouter (no data router required).
 *
 * Intercepts:
 * - React Router navigations (Link clicks, useNavigate calls) via navigator monkey-patching
 * - Browser back/forward buttons via popstate event
 * - Tab close / hard refresh via beforeunload event
 *
 * @param shouldBlock - Whether navigation should be blocked
 * @param onBlock - Callback invoked when navigation is blocked. Receives a proceed() function
 *                  that the caller should invoke to allow the blocked navigation to continue.
 */
export function useNavigationBlock(
  shouldBlock: boolean,
  onBlock: (proceed: () => void) => void
) {
  const { navigator } = useContext(NavigationContext);
  const shouldBlockRef = useRef(shouldBlock);
  const onBlockRef = useRef(onBlock);

  // Keep refs in sync to avoid stale closures
  useEffect(() => {
    shouldBlockRef.current = shouldBlock;
  }, [shouldBlock]);

  useEffect(() => {
    onBlockRef.current = onBlock;
  }, [onBlock]);

  // Intercept React Router's navigator.push and navigator.replace
  useEffect(() => {
    if (!shouldBlock) return;

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    navigator.push = (...args: Parameters<typeof navigator.push>) => {
      if (shouldBlockRef.current && !globalBypass) {
        onBlockRef.current(() => {
          navigator.push = originalPush;
          navigator.replace = originalReplace;
          originalPush.apply(navigator, args);
        });
      } else {
        originalPush.apply(navigator, args);
      }
    };

    navigator.replace = (...args: Parameters<typeof navigator.replace>) => {
      if (shouldBlockRef.current && !globalBypass) {
        onBlockRef.current(() => {
          navigator.push = originalPush;
          navigator.replace = originalReplace;
          originalReplace.apply(navigator, args);
        });
      } else {
        originalReplace.apply(navigator, args);
      }
    };

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [shouldBlock, navigator]);

  // Intercept browser back/forward buttons via popstate
  useEffect(() => {
    if (!shouldBlock) return;

    // Push a sentry entry so we can detect back button
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (shouldBlockRef.current && !globalBypass) {
        // Re-push the sentry entry to keep user on the page
        window.history.pushState(null, '', window.location.href);
        onBlockRef.current(() => {
          // User chose to leave: go back twice (past the sentry entry + original back)
          window.history.go(-2);
        });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [shouldBlock]);

  // Intercept tab close / hard refresh
  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockRef.current && !globalBypass) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlock]);
}
